'use client';

import { Box, Divider, Stack, Typography } from '@mui/material';
import { Provider, type AzureVnetConnectivityConfig } from '@repo/api-gw-sdk';
import { useMemo, useState } from 'react';

import { ButtonWithIcon } from '@/components/buttons/buttonWithIcon';
import { OptionMenu } from '@/components/option-menu/option-menu';
import { StepContainer } from '@/components/wizard/StepContainer';
import type { StepProps } from '@/components/wizard/StepProps';

import { AzureCustomizeNetworkBottomDialog } from './azureCustomizeNetworkBottomDialog';
import { AzureVnetSelectionDialog } from './azureVnetSelectionDialog';
import { DeleteRestoreServerConfigDialog } from './deleteRestoreServerConfigDialog';
import type { RestoreAccountConfigState } from './restoreAccountConfigFlow';

function updateAzureVnetConfigs(
  state: RestoreAccountConfigState,
  updater: (
    configs: AzureVnetConnectivityConfig[]
  ) => AzureVnetConnectivityConfig[]
): RestoreAccountConfigState {
  const current = state.config.config.azure?.vnetConfigurations || [];
  return {
    ...state,
    config: {
      ...state.config,
      config: {
        ...state.config.config,
        azure: {
          ...state.config.config.azure,
          vnetConfigurations: updater(current),
        },
      },
    },
  };
}

export const AzureRestoreAccountConfigVnetSelection = (
  props: StepProps<RestoreAccountConfigState>
) => {
  const [addButtonEl, setAddButtonEl] = useState<HTMLElement | null>(null);
  const [configForDeletion, setConfigForDeletion] = useState<
    AzureVnetConnectivityConfig | undefined
  >();
  const [showSelectionDialog, setShowSelectionDialog] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState<
    AzureVnetConnectivityConfig | undefined
  >();
  const [editedConfig, setEditedConfig] = useState<
    AzureVnetConnectivityConfig | undefined
  >();

  const vnetConfigs = useMemo(
    () => props.wizardState.config.config.azure?.vnetConfigurations || [],
    [props.wizardState.config.config.azure?.vnetConfigurations]
  );

  const sortedConfigs = useMemo(
    () => [...vnetConfigs].sort((a, b) => a.region.localeCompare(b.region)),
    [vnetConfigs]
  );

  const hasConfigs = vnetConfigs.length > 0;

  return (
    <StepContainer
      hideStepper
      onBackClick={props.back}
      canGoNext={() => true}
      nextButtonText='Save Changes'
      backButtonText='Cancel'
      onNextClick={() => props.finish()}
      sx={{ height: '100%' }}
    >
      <Stack sx={{ height: '100%' }}>
        <Stack className='px-[50px]' direction='row' flexGrow={1}>
          <Stack className='w-[280px]' gap='40px'>
            {hasConfigs && (
              <Stack gap='40px'>
                <Typography>Customized VNets</Typography>
                <Stack gap='24px'>
                  {sortedConfigs.map((config) => (
                    <Stack key={config.region}>
                      <Stack
                        justifyContent='space-between'
                        direction='row'
                        alignItems='center'
                        className='pr-[22px] cursor-pointer'
                        sx={{
                          borderRight:
                            selectedConfig === config
                              ? '2px solid black'
                              : '2px solid transparent',
                        }}
                        onClick={() => setSelectedConfig(config)}
                      >
                        <Stack>
                          <Typography>{config.vnetName}</Typography>
                          <Typography variant='body2' color='text.secondary'>
                            {config.region}
                          </Typography>
                        </Stack>
                        <OptionMenu
                          options={[
                            {
                              id: 'delete-config',
                              text: 'Delete',
                              menuItemProps: {
                                onClick: () => setConfigForDeletion(config),
                              },
                            },
                          ]}
                        />
                      </Stack>
                    </Stack>
                  ))}
                </Stack>
              </Stack>
            )}
            <Box>
              <ButtonWithIcon
                ref={setAddButtonEl}
                variant='outlined'
                icon='material-symbols-add-rounded'
                text={hasConfigs ? 'Customize Another VNet' : 'Customize VNet'}
                onClick={() => setShowSelectionDialog(true)}
              />
            </Box>
            {hasConfigs && (
              <>
                <Divider className='mt-[20px] ml-[-100px]' />
                <Typography
                  className='w-[208px]'
                  fontWeight='300'
                  lineHeight='20px'
                >
                  For all other VNets, Eon uses its own managed network.
                </Typography>
              </>
            )}
            <DeleteRestoreServerConfigDialog
              name={configForDeletion?.vnetName || ''}
              provider={Provider.Azure}
              isOpen={!!configForDeletion}
              onCancel={() => setConfigForDeletion(undefined)}
              onConfirm={() => {
                setConfigForDeletion(undefined);
                if (selectedConfig === configForDeletion) {
                  setSelectedConfig(undefined);
                }
                props.setWizardState((state) =>
                  updateAzureVnetConfigs(state, (configs) =>
                    configs.filter((x) => x !== configForDeletion)
                  )
                );
              }}
            />
            {showSelectionDialog && (
              <AzureVnetSelectionDialog
                accountId={props.wizardState.account.id}
                anchorEl={addButtonEl}
                onClose={(config) => {
                  setShowSelectionDialog(false);
                  if (
                    !config ||
                    vnetConfigs.some((c) => c.region === config.region)
                  ) {
                    return;
                  }
                  props.setWizardState((state) =>
                    updateAzureVnetConfigs(state, (configs) => [
                      ...configs,
                      config,
                    ])
                  );
                  setSelectedConfig(config);
                  setEditedConfig(config);
                }}
              />
            )}
          </Stack>
          {hasConfigs && <Divider flexItem orientation='vertical' />}
          {selectedConfig && (
            <Stack className='ml-[40px]' gap='40px' flexGrow={1}>
              <Stack direction='row' justifyContent='space-between'>
                <Stack direction='row' gap='4px'>
                  <Typography component='span'>Restore server in</Typography>
                  <Typography component='span' className='font-semibold'>
                    {selectedConfig.vnetName}
                  </Typography>
                  <Typography component='span'>
                    will use these settings:
                  </Typography>
                </Stack>
                <ButtonWithIcon
                  variant='outlined'
                  icon='material-symbols-stylus-outline'
                  text='Edit VNet'
                  onClick={() => setEditedConfig(selectedConfig)}
                />
              </Stack>
              <Stack direction='row' gap='100px'>
                <Stack gap='36px' className='w-[240px]'>
                  <Typography className='font-semibold'>Details</Typography>
                </Stack>
                <Stack flexGrow={1} gap='24px'>
                  <DetailRow label='Region' value={selectedConfig.region} />
                  <DetailRow
                    label='Resource group'
                    value={selectedConfig.resourceGroup}
                  />
                  <DetailRow
                    label={
                      selectedConfig.subnetNames?.length === 1
                        ? 'Subnet'
                        : 'Subnets'
                    }
                    value={selectedConfig.subnetNames?.join(', ') || '—'}
                  />
                </Stack>
              </Stack>
              <Divider className='my-[20px]' />
            </Stack>
          )}
        </Stack>
      </Stack>
      {editedConfig && (
        <AzureCustomizeNetworkBottomDialog
          accountId={props.wizardState.account.id}
          config={editedConfig}
          onCancel={() => {
            if (!editedConfig?.subnetNames?.length) {
              props.setWizardState((state) =>
                updateAzureVnetConfigs(state, (configs) =>
                  configs.filter((x) => x !== editedConfig)
                )
              );
              if (selectedConfig === editedConfig) {
                setSelectedConfig(undefined);
              }
            }
            setEditedConfig(undefined);
          }}
          onSave={(updatedConfig) => {
            props.setWizardState((state) =>
              updateAzureVnetConfigs(state, (configs) =>
                configs.map((x) => (x === editedConfig ? updatedConfig : x))
              )
            );
            setSelectedConfig(updatedConfig);
            setEditedConfig(undefined);
          }}
        />
      )}
    </StepContainer>
  );
};

const DetailRow = ({ label, value }: { label: string; value: string }) => (
  <Stack direction='row' gap='40px'>
    <Typography className='w-[150px]' color='text.secondary'>
      {label}
    </Typography>
    <Typography>{value}</Typography>
  </Stack>
);
